import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, MapPin, Clock, AlertTriangle, Calendar } from 'lucide-react';
import { Doctor, Staff, Room, ScheduleItem, getDoctorPreferences, DoctorStaffPreferenceWithDetails } from '@/services/adminService';
import { format } from 'date-fns';

interface BackupDAScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: Date;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  scheduleItems: ScheduleItem[];
}

interface DAStatus {
  staffId: string;
  status: 'assigned' | 'conflict' | 'unavailable' | 'available';
  assignedTo?: string; // doctor id
  conflictReason?: string;
}

interface PreferenceDisplay {
  rank: 1 | 2 | 3 | 4;
  staffId: string | null;
  staffName: string;
  status: DAStatus['status'];
  assignedTo?: string;
}

const BackupDAScheduleModal = ({
  isOpen,
  onClose,
  currentDate,
  doctors,
  staff,
  rooms,
  scheduleItems
}: BackupDAScheduleModalProps) => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('09:00');
  const [allPreferences, setAllPreferences] = useState<DoctorStaffPreferenceWithDetails[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter active doctors and dental assistants (including clinic supervisors who can also be backup DAs)
  const activeDoctors = doctors.filter(d => d.status === 'active');
  const dentalAssistants = staff.filter(s => 
    (s.role.toLowerCase().includes('dental assistant') || 
     s.role.toLowerCase().includes('clinic supervisor')) && 
    s.status === 'active'
  );

  // Time slots for selection
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  }, []);

  // Load all doctor preferences
  useEffect(() => {
    if (isOpen) {
      loadAllPreferences();
    }
  }, [isOpen]);

  const loadAllPreferences = async () => {
    setLoading(true);
    try {
      const preferences = await Promise.all(
        activeDoctors.map(async (doctor) => {
          const prefs = await getDoctorPreferences(doctor.id);
          return prefs;
        })
      );
      setAllPreferences(preferences.flat());
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get DA status for a specific time slot
  const getDAStatus = (staffId: string, timeSlot: string): DAStatus => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    
    // Check if DA is on leave or MC
    const leaveSchedules = scheduleItems.filter(item => 
      item.date === dateStr && 
      item.staffId === staffId && 
      (item.type === 'leave' || item.type === 'mc')
    );
    
    if (leaveSchedules.length > 0) {
      return {
        staffId,
        status: 'unavailable',
        conflictReason: leaveSchedules[0].type === 'leave' ? 'On Leave' : 'Medical Certificate'
      };
    }

    // Check if DA is assigned to any doctor at this time
    const assignments = scheduleItems.filter(item => 
      item.date === dateStr && 
      item.staffId === staffId &&
      item.startTime <= timeSlot &&
      item.endTime > timeSlot &&
      item.doctorId
    );

    if (assignments.length === 0) {
      return { staffId, status: 'available' };
    }

    if (assignments.length === 1) {
      return {
        staffId,
        status: 'assigned',
        assignedTo: assignments[0].doctorId
      };
    }

    // Multiple assignments = conflict
    return {
      staffId,
      status: 'conflict',
      conflictReason: 'Multiple assignments'
    };
  };

  // Get doctor's preferences with status
  const getDoctorPreferencesWithStatus = (doctorId: string): PreferenceDisplay[] => {
    const doctorPrefs = allPreferences.filter(p => p.doctor_id === doctorId);
    
    return [1, 2, 3, 4].map(rank => {
      const pref = doctorPrefs.find(p => p.preference_rank === rank);
      if (!pref) {
        return {
          rank: rank as 1 | 2 | 3 | 4,
          staffId: null,
          staffName: 'Not set',
          status: 'available'
        };
      }

      const status = getDAStatus(pref.staff_id, selectedTimeSlot);
      const staffMember = dentalAssistants.find(s => s.id === pref.staff_id);
      
      return {
        rank: rank as 1 | 2 | 3 | 4,
        staffId: pref.staff_id,
        staffName: staffMember ? (staffMember.short_name || staffMember.name) : 'Unknown',
        status: status.status,
        assignedTo: status.assignedTo
      };
    });
  };

  // Get conflicts summary
  const getConflicts = () => {
    const conflicts: string[] = [];
    const mainNurseAssignments: Record<string, string[]> = {};

    // Check for main nurse conflicts (multiple doctors having same DA as rank 1)
    allPreferences
      .filter(p => p.preference_rank === 1)
      .forEach(pref => {
        if (!mainNurseAssignments[pref.staff_id]) {
          mainNurseAssignments[pref.staff_id] = [];
        }
        mainNurseAssignments[pref.staff_id].push(pref.doctor_id);
      });

    Object.entries(mainNurseAssignments).forEach(([staffId, doctorIds]) => {
      if (doctorIds.length > 1) {
        const staffMember = dentalAssistants.find(s => s.id === staffId);
        const staffName = staffMember ? (staffMember.short_name || staffMember.name) : 'Unknown';
        conflicts.push(`${staffName} is main nurse for ${doctorIds.length} doctors`);
      }
    });

    return conflicts;
  };

  // Status color coding
  const getStatusColor = (status: DAStatus['status'], isMainNurse: boolean = false) => {
    switch (status) {
      case 'assigned':
        return isMainNurse ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800';
      case 'conflict':
        return 'bg-orange-100 text-orange-800';
      case 'unavailable':
        return 'bg-gray-100 text-gray-500 line-through';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const getRankLabel = (rank: number) => {
    switch (rank) {
      case 1: return 'Main Nurse';
      case 2: return 'Primary Backup';
      case 3: return 'Secondary Backup';
      case 4: return 'Third Backup';
      default: return `Rank ${rank}`;
    }
  };

  const renderByDoctorView = () => (
    <div className="space-y-4">
      {activeDoctors.map(doctor => {
        const preferences = getDoctorPreferencesWithStatus(doctor.id);
        
        return (
          <Card key={doctor.id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">
                Dr {doctor.short_name || doctor.name} - {doctor.specialization}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {preferences.map(pref => (
                  <div key={pref.rank} className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">
                      {getRankLabel(pref.rank)}
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`${getStatusColor(pref.status, pref.rank === 1)} text-xs px-2 py-1`}
                    >
                      {pref.staffName}
                    </Badge>
                    {pref.assignedTo && pref.assignedTo !== doctor.id && (
                      <div className="text-xs text-orange-600 mt-1">
                        Assigned to other doctor
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderByRoomView = () => (
    <div className="space-y-4">
      {rooms.filter(r => r.status === 'active').map(room => {
        // Find doctors assigned to this room at selected time
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const roomAssignments = scheduleItems.filter(item =>
          item.date === dateStr &&
          item.roomId === room.id &&
          item.startTime <= selectedTimeSlot &&
          item.endTime > selectedTimeSlot &&
          item.doctorId
        );

        return (
          <Card key={room.id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>{room.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {roomAssignments.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No assignments at {selectedTimeSlot}
                </div>
              ) : (
                <div className="space-y-3">
                  {roomAssignments.map(assignment => {
                    const doctor = activeDoctors.find(d => d.id === assignment.doctorId);
                    if (!doctor) return null;

                    const preferences = getDoctorPreferencesWithStatus(doctor.id);
                    
                    return (
                      <div key={assignment.id} className="border rounded p-3">
                        <div className="font-medium text-sm mb-2">
                          Dr {doctor.short_name || doctor.name}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {preferences.map(pref => (
                            <Badge 
                              key={pref.rank}
                              variant="outline" 
                              className={`${getStatusColor(pref.status, pref.rank === 1)} text-xs`}
                            >
                              {pref.staffName}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderByTimeslotView = () => (
    <div className="space-y-4">
      {dentalAssistants.map(staffMember => {
        const status = getDAStatus(staffMember.id, selectedTimeSlot);
        const assignedDoctor = status.assignedTo ? 
          activeDoctors.find(d => d.id === status.assignedTo) : null;
        
        // Find which doctors have this DA in their preferences
        const preferenceRelationships = allPreferences
          .filter(p => p.staff_id === staffMember.id)
          .map(p => {
            const doctor = activeDoctors.find(d => d.id === p.doctor_id);
            return {
              doctor: doctor ? (doctor.short_name || doctor.name) : 'Unknown',
              rank: p.preference_rank
            };
          });

        return (
          <Card key={staffMember.id}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Badge 
                    variant="outline" 
                    className={`${getStatusColor(status.status)} font-medium`}
                  >
                    {staffMember.short_name || staffMember.name}
                  </Badge>
                  {assignedDoctor && (
                    <span className="text-sm text-muted-foreground">
                      → Dr {assignedDoctor.short_name || assignedDoctor.name}
                    </span>
                  )}
                  {status.conflictReason && (
                    <span className="text-sm text-red-600">
                      ({status.conflictReason})
                    </span>
                  )}
                </div>
                <div className="text-right">
                  {preferenceRelationships.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Preferred by: {preferenceRelationships.map(p => 
                        `Dr ${p.doctor} (#${p.rank})`
                      ).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const conflicts = getConflicts();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Backup DA Schedule - {format(currentDate, 'PPPP')}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading preferences...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Time Slot Selection */}
            <div className="flex items-center space-x-4">
              <Clock className="h-4 w-4" />
              <Select value={selectedTimeSlot} onValueChange={setSelectedTimeSlot}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(slot => (
                    <SelectItem key={slot} value={slot}>
                      {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                Select time to view DA assignments
              </span>
            </div>

            {/* Conflicts Alert */}
            {conflicts.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Preference Conflicts Detected:</strong>
                  <ul className="mt-1 list-disc list-inside">
                    {conflicts.map((conflict, index) => (
                      <li key={index}>{conflict}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Status Legend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Status Legend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-green-600 text-white">Currently Assigned (Main)</Badge>
                  <Badge className="bg-green-100 text-green-800">Currently Assigned (Backup)</Badge>
                  <Badge className="bg-orange-100 text-orange-800">Assigned to Other Doctor</Badge>
                  <Badge className="bg-gray-100 text-gray-500 line-through">Unavailable (Leave/MC)</Badge>
                  <Badge className="bg-gray-50 text-gray-600">Available</Badge>
                </div>
              </CardContent>
            </Card>

            {/* View Tabs */}
            <Tabs defaultValue="doctor" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="doctor">By Doctor</TabsTrigger>
                <TabsTrigger value="room">By Room</TabsTrigger>
                <TabsTrigger value="timeslot">By Timeslot</TabsTrigger>
              </TabsList>
              
              <TabsContent value="doctor" className="mt-4">
                {renderByDoctorView()}
              </TabsContent>
              
              <TabsContent value="room" className="mt-4">
                {renderByRoomView()}
              </TabsContent>
              
              <TabsContent value="timeslot" className="mt-4">
                {renderByTimeslotView()}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BackupDAScheduleModal;